//@ts-nocheck
import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
  useEffect,
} from "react";
import toast from "react-hot-toast";
import { api } from "../services/api";
import { useNavigate } from "react-router-dom";
import { useAuth } from "./AuthContext"; // adjust path
import { t } from "i18next";

export interface SamayPravahGraph {
  movement: { type: string; intensity: number };
  phase_weight: { type: string; intensity: number };
  flow_direction: { type: string; intensity: number };
  // Optional — only ever populated by the "Astria India V2" category's
  // Samay Pravah lane (see AstriaIndiaV2SamayData below). The original
  // "Astria India" Samay Pravah lane never sets these, so it is unaffected.
  dasha?: string;
  sub_dasha?: string;
}

export interface VyaktivaDarshanData {
  vyaktitva_darshan: {
    core_nature: string;
    emotional_pattern: string;
    inner_rhythm: string;
    fear_tendency: string;
    desire_tendency: string;
    relationship_style: string;
    pada_code: string;
    pada_traits: string;
  };
}

// Astria India v2 dashboard — structured payload shapes already produced by
// the existing Bhavna Drishti / Upay Marg / Sambandh Taal-Mel backend
// engines but previously dropped by the stream reader below (only
// samayPravahGraph/astriaKoreaV2Data/etc. were captured). Purely additive.
export interface BhavnaDrishtiData {
  bhavna_drishti: {
    emotional_state: string;
    root_pattern: string;
    current_weight: string;
    inner_room_imagery: string;
    soft_landing: string;
  };
}

export interface UpayMargData {
  current_energy: string;
  vedic_reflection: string;
  suggested_upay: { title: string; description: string; category: string }[];
  gentle_closing: string;
}

export interface SambandhTaalMelData {
  sambandh_taalmel: {
    rhythm_between: string;
    harmony_level: string;
    friction_point: string;
    timing_alignment: string;
    connection_path: string;
  };
}

// Astria India V2 — one category, 7 subcategories, each with its own JSON
// shape (see helper/astriaIndiaV2Service.js on the backend). Kept as a
// single loosely-typed union per subcategory since the schema differs by
// tab; each tab component narrows to the fields it expects.
// Astria India V3 signature-layer fields (helper/astriaIndiaV3Service.js /
// Update.txt's ADD-ON MODULE) — optional on every lane's interface below
// since V2 responses never populate them. highlight/micro_imagery apply to
// all 7 lanes; astro_soft_influence and clarity_point are only ever sent by
// the lanes the backend scopes them to (each interface below only declares
// the ones that lane can actually receive).
export interface AstriaIndiaV3SignatureFields {
  highlight?: string;
  micro_imagery?: string;
}

export interface AstriaIndiaV2SambandhData extends AstriaIndiaV3SignatureFields {
  rhythm_between: string;
  harmony_level: string;
  friction_point: string;
  timing_alignment: string;
  connection_path: string;
}

export interface AstriaIndiaV2VivahData extends AstriaIndiaV3SignatureFields {
  opening: string;
  compatibility_snapshot: string;
  recommended_windows: { window_label: string; feeling: string }[];
  timing_to_approach_gently: string;
  closing: string;
}

export interface AstriaIndiaV2UpayData extends AstriaIndiaV3SignatureFields {
  current_energy: string;
  vedic_reflection: string;
  suggested_upay: { title: string; description: string; category: string }[];
  gentle_closing: string;
  clarity_point?: string;
}

export interface AstriaIndiaV2BhavnaData extends AstriaIndiaV3SignatureFields {
  emotional_state: string;
  root_pattern: string;
  current_weight: string;
  inner_room_imagery: string;
  soft_landing: string;
  actions: string[];
  astro_soft_influence?: string;
  clarity_point?: string;
}

export interface AstriaIndiaV2VyaktitvaData extends AstriaIndiaV3SignatureFields {
  core_nature: string;
  emotional_pattern: string;
  inner_rhythm: string;
  relationship_style: string;
  growth_invitation: string;
  actions: string[];
  astro_soft_influence?: string;
}

export interface AstriaIndiaV2SamayData extends AstriaIndiaV3SignatureFields {
  movement: { type: string; intensity: number };
  phase_weight: { type: string; intensity: number };
  flow_direction: { type: string; intensity: number };
  dasha?: string;
  sub_dasha?: string;
  astro_soft_influence?: string;
  clarity_point?: string;
}

export interface AstriaIndiaV2AapkaNoteData extends AstriaIndiaV3SignatureFields {
  reflection: string;
  emotional_thread: string;
  gentle_note: string;
  clarity_point?: string;
}

export type AstriaIndiaV2Data =
  | AstriaIndiaV2SambandhData
  | AstriaIndiaV2VivahData
  | AstriaIndiaV2UpayData
  | AstriaIndiaV2BhavnaData
  | AstriaIndiaV2VyaktitvaData
  | AstriaIndiaV2SamayData
  | AstriaIndiaV2AapkaNoteData;

// Astria Vietnam — one category, 5 subcategories (Birth Chart, Lucky Day,
// Compatibility, Space Energy, Tarot Reading), each with its own JSON shape
// (see backend/helper/vietnam/astriaVietnamPromptService.js). Distinct from
// "Astria Vietnam V2" (unrelated, plain-text-only emotional check-in — no
// structured data field at all). Kept as a loosely-typed union per lane,
// same pattern as AstriaIndiaV2Data above; each tab component narrows to
// the fields it expects.
export interface AstriaVietnamBirthChartData {
  summary: string;
  cung_menh_meaning: string;
  cung_than_meaning: string;
  hoa_khi_effect: string;
  current_cycle: string;
  guidance: string;
}

export interface AstriaVietnamLuckyDayData {
  lunar_date: string;
  suitability: string;
  best_for: string;
  caution_note: string;
  guidance: string;
}

export interface AstriaVietnamCompatibilityData {
  // Omitted by the backend (not just empty) when partner DOB wasn't
  // provided — never fabricate a score client-side, only render when present.
  compatibility_score?: number;
  rhythm_between: string;
  harmony_level: string;
  friction_point: string;
  connection_path: string;
}

export interface AstriaVietnamSpaceEnergyData {
  space_reading: string;
  // Omitted when the user didn't provide a direction.
  direction_note?: string;
  energy_suggestion: string;
  guidance: string;
}

export interface AstriaVietnamTarotData {
  card_name: string;
  card_meaning: string;
  reflection: string;
  guidance: string;
}

export type AstriaVietnamData =
  | AstriaVietnamBirthChartData
  | AstriaVietnamLuckyDayData
  | AstriaVietnamCompatibilityData
  | AstriaVietnamSpaceEnergyData
  | AstriaVietnamTarotData;

// Astria UK V2 — one category ("Astria UK V2"), 9 subcategories, each with
// its own structured JSON shape (see backend/helper/astriaUKV2Service.js
// UK_V2_MODULES registry — field names below match that file's jsonSkeleton
// per module exactly). Energy Match, MateScan, Relationship, and
// Compatibility are two-person readings; the rest are single-person.
export interface AstriaUKV2EnergyMatchData {
  opening: string;
  current_energy: string;
  connection_pattern: string;
  gap_analysis: string;
  heart_action_plan: { today: string[]; this_week: string[] };
  where_this_can_go: string;
}

export interface AstriaUKV2MateScanData {
  opening: string;
  vibe_check: string;
  compatibility_snap: string;
  chaos_factor: string;
  fun_action_plan: { today: string[]; this_week: string[] };
  closing: string;
}

export interface AstriaUKV2RelationshipData {
  opening: string;
  current_vibe: string;
  strengths: string;
  gentle_adjustment: string;
  today_action: string;
  closing: string;
}

export interface AstriaUKV2CompanionTalkData {
  reflection: string;
  observation: string;
  question: string;
}

export interface AstriaUKV2CosmicData {
  reading: string;
}

export interface AstriaUKV2DailyFlowData {
  reading: string;
}

export interface AstriaUKV2ZodiacPersonalityData {
  core_trait: string;
  everyday_style: string;
  where_it_serves_you: string;
  where_it_trips_you_up: string;
  closing: string;
}

export interface AstriaUKV2CompatibilityData {
  reading: string;
}

export interface AstriaUKV2EmotionalRhythmData {
  morning: string;
  midday: string;
  afternoon: string;
  evening: string;
  night: string;
}

export interface AstriaUKV2ChironData {
  reading: string;
}

export type AstriaUKV2Data =
  | AstriaUKV2EnergyMatchData
  | AstriaUKV2MateScanData
  | AstriaUKV2RelationshipData
  | AstriaUKV2CompanionTalkData
  | AstriaUKV2CosmicData
  | AstriaUKV2DailyFlowData
  | AstriaUKV2ZodiacPersonalityData
  | AstriaUKV2CompatibilityData
  | AstriaUKV2EmotionalRhythmData
  | AstriaUKV2ChironData;

// ─── Astria Mexico — structured per-module data, shapes match the JSON
// skeletons in backend/helper/astriaMexicoService.js's MX_MODULES registry.

export interface AstriaMexicoEnergyMatchData {
  opening: string;
  current_energy: string;
  connection_pattern: string;
  gap_analysis: string;
  heart_action_plan: { today: string[]; this_week: string[] };
  where_this_can_go: string;
}

export interface AstriaMexicoCompatibilityData {
  reading: string;
  strengths: string;
  challenges: string;
}

export interface AstriaMexicoCosmicMessageData {
  message: string;
  reflection_prompt: string;
}

export interface AstriaMexicoRelationshipData {
  opening: string;
  current_vibe: string;
  strengths: string;
  gentle_adjustment: string;
  today_action: string;
  closing: string;
}

export interface AstriaMexicoCompanionTalkData {
  reflection: string;
  observation: string;
  question: string;
}

export interface AstriaMexicoDailyFlowData {
  reading: string;
}

export interface AstriaMexicoZodiacPersonalityData {
  core_trait: string;
  everyday_style: string;
  where_it_serves_you: string;
  where_it_trips_you_up: string;
  closing: string;
}

export type AstriaMexicoData =
  | AstriaMexicoEnergyMatchData
  | AstriaMexicoCompatibilityData
  | AstriaMexicoCosmicMessageData
  | AstriaMexicoRelationshipData
  | AstriaMexicoCompanionTalkData
  | AstriaMexicoDailyFlowData
  | AstriaMexicoZodiacPersonalityData;

export interface Message {
  id: string;
  content: string;
  sender: "user" | "ai";
  timestamp: Date;
  categoryId?: string;
  subCategoryId?: string;
  samayPravahGraph?: SamayPravahGraph;
  vyaktivaDarshanData?: VyaktivaDarshanData;
  astriaKoreaV2Data?: any;
  astriaJapanHybridData?: any;
  bhavnaDrishtiData?: BhavnaDrishtiData;
  upayMargData?: UpayMargData;
  sambandhTaalMelData?: SambandhTaalMelData;
  astriaIndiaV2Data?: AstriaIndiaV2Data;
  astriaVietnamData?: AstriaVietnamData;
  astriaUKV2Data?: AstriaUKV2Data;
  astriaMexicoData?: AstriaMexicoData;
  // Astria Philippines V2 / Astria Indonesia V2 — deterministic copy-pack
  // selection metadata (see helper/philippinesIndonesiaV2Shared.js on the
  // backend). No structured fields to render — message.content is already
  // the full plain-text line; this is only which tab/line was picked, kept
  // for parity with the backend contract.
  phVnIdV2Data?: { tab: string; lineIndex: number };
}

export interface Chat {
  id: string;
  title: string;
  messages: Message[];
  categoryId?: string;
  subCategoryId?: string;
  createdAt: Date;
  updatedAt: Date;
  userId?: string;
  gccCompatibilityData?: any;
  japanCompatibilityData?: any;
  indonesiaCompatibilityData?: any;
}

interface ChatContextType {
  chats: Chat[];
  currentChat: Chat | null;
  isLoading: boolean;
  isTyping: boolean;
  addChat: (chat: Chat) => Promise<void>;
  setCurrentChat: (chat: Chat | null) => void;
  addMessage: (chatId: string, message: Message) => Promise<void>;
  updateMessage: (chatId: string, messageId: string, content: string) => void;
  deleteChat: (chatId: string) => Promise<boolean>;
  startNewChat: (
    categoryId?: string,
    subCategoryId?: string,
    initialMessage?: string,
  ) => Promise<Chat>; // Update this
  openChat: (chatId: string) => Promise<void>;
  sendMessage: (content: string, chatId?: string) => Promise<void>;
  sendMessageStream: (
    content: string,
    chatId?: string,
    categoryId?: string | null,
    subCategoryId?: string | null,
    spanishTone?: string | null,
    japan3BoxSelf?: object | null,
    japan3BoxPartner?: object | null,
    korea3BoxSelf?: object | null,
    korea3BoxPartner?: object | null,
    gcc3BoxSelf?: object | null,
    gcc3BoxPartner?: object | null,
    indonesia3BoxSelf?: object | null,
    indonesia3BoxPartner?: object | null,
    // "hybrid" (default) | "traditional" — Astria Korea Hybrid toggle above
    // the message input, see ChatInterface.tsx's isAstriaKoreaHybrid block.
    koreaHybridMode?: string | null,
    // "hybrid" (default) | "traditional" — Astria Japan Hybrid toggle above
    // the message input, see ChatInterface.tsx's isAstriaJapanHybrid block.
    japanHybridMode?: string | null,
  ) => Promise<string | null>;
  fetchChats: () => Promise<void>;
  fetchSingleChat: (chatId: string) => Promise<Chat | null>;
  resetChatState: () => void;
}

const ChatContext = createContext<ChatContextType | undefined>(undefined);

export const useChat = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error("useChat must be used within a ChatProvider");
  }
  return context;
};

interface ChatProviderProps {
  children: ReactNode;
}

export const ChatProvider: React.FC<ChatProviderProps> = ({ children }) => {
  const [chats, setChats] = useState<Chat[]>([]);
  const [currentChat, setCurrentChat] = useState<Chat | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();

  const convertToChatFormat = useCallback((backendChat: any): Chat => {
    console.log("Converting backend chat to format:", backendChat);

    const messages: Message[] = [];

    if (backendChat.chats && Array.isArray(backendChat.chats)) {
      console.log(
        "Processing chats array with length:",
        backendChat.chats.length,
      );

      backendChat.chats.forEach((chat: any, index: number) => {
        console.log(`Chat ${index}:`, {
          userMessage: chat.userMessage,
          aiResponse: chat.aiResponse,
          timestamp: chat.timestamp,
        });

        // Only add user message if it exists and isn't empty
        if (chat.userMessage && chat.userMessage.trim()) {
          messages.push({
            id: `${backendChat._id}_user_${index}_${Date.now()}`,
            content: chat.userMessage,
            sender: "user",
            timestamp: new Date(chat.timestamp || backendChat.updatedAt),
            categoryId: backendChat.categoryId,
            subCategoryId: backendChat.subCategoryId,
          });
        }

        // Only add AI response if it exists and isn't empty
        if (chat.aiResponse && chat.aiResponse.trim()) {
          messages.push({
            id: `${backendChat._id}_ai_${index}_${Date.now()}`,
            content: chat.aiResponse,
            sender: "ai",
            timestamp: new Date(chat.timestamp || backendChat.updatedAt),
            categoryId: backendChat.categoryId,
            subCategoryId: backendChat.subCategoryId,
            astriaKoreaV2Data: chat.astriaKoreaV2Data || undefined,
            astriaJapanHybridData: chat.astriaJapanHybridData || undefined,
          });
        }
      });
    }

    console.log("Final messages array:", messages);

    return {
      id: backendChat._id,
      title: backendChat.sessionTitle || "New Chat",
      messages,
      categoryId: backendChat.categoryId,
      subCategoryId: backendChat.subCategoryId,
      userId: backendChat.userId,
      createdAt: new Date(backendChat.createdAt),
      updatedAt: new Date(backendChat.updatedAt),
    };
  }, []);

  const fetchChats = useCallback(async () => {
    try {
      setIsLoading(true);
      const userId = localStorage.getItem("userId");

      if (!userId) {
        console.log("No user ID found");
        return;
      }

      const response = await api.get("/backend/chats", {
        params: { userId },
      });

      if (response.data.success) {
        const formattedChats = response.data.data.map((chat: any) =>
          convertToChatFormat(chat),
        );
        setChats(formattedChats);
      }
    } catch (error: any) {
      console.error("Failed to fetch chats:", error);
      const specificErrorMessage =
        error.response?.data?.message ||
        error.message ||
        t("toast.chatContext.chatLoadFailed") ||
        "Failed to load chats";
      toast.error(specificErrorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [convertToChatFormat]);

  const fetchSingleChat = useCallback(
    async (chatId: string): Promise<Chat | null> => {
      try {
        const response = await api.get("/backend/chats", {
          params: { chatId },
        });

        if (response.data.success && response.data.data) {
          const chat = convertToChatFormat(response.data.data);
          return chat;
        }
        return null;
      } catch (error: any) {
        console.error("Failed to fetch chat:", error);
        const specificErrorMessage =
          error.response?.data?.message ||
          error.message ||
          t("toast.chatContext.chatLoadFailed") ||
          "Failed to load chats";
        toast.error(specificErrorMessage);
        return null;
      }
    },
    [convertToChatFormat],
  );

  const resetChatState = useCallback(() => {
    setChats([]);
    setCurrentChat(null);
    setIsTyping(false);
    setIsLoading(false);
  }, []);

  useEffect(() => {
    // whenever logged-in user changes -> clear old state and load new chats
    resetChatState();
    if (user?._id) fetchChats();
  }, [user?._id, resetChatState, fetchChats]);

  const openChat = useCallback(
    async (chatId: string) => {
      try {
        setIsLoading(true);

        if (chatId.startsWith("t-")) {
          const chat = chats.find((c) => c.id === chatId);
          if (chat) setCurrentChat(chat);
          return;
        }

        const chat = await fetchSingleChat(chatId);
        if (chat) {
          setCurrentChat(chat);

          // Optional but recommended: keep chats list updated with full chat
          setChats((prev) => {
            const exists = prev.some((c) => c.id === chat.id);
            return exists
              ? prev.map((c) => (c.id === chat.id ? chat : c))
              : [chat, ...prev];
          });
        } else {
          toast.error(
            t("toast.chatContext.chatLoadFailed") || "Failed to load chats",
          );
        }
      } catch (err) {
        console.error("openChat error:", err);
        toast.error(
          t("toast.chatContext.chatLoadFailed") || "Failed to load chats",
        );
      } finally {
        setIsLoading(false);
      }
    },
    [chats, fetchSingleChat],
  );

  // Function to classify user group and adjust logic
  const getHealjaiPersona = () => {
    try {
      const tempUser = localStorage.getItem("user");
      const userData = tempUser ? JSON.parse(tempUser) : null;

      if (!userData || !userData.dob) {
        // Handle missing data case
        return {
          category: "GENERAL",
          focusPoints: ["Career", "Love", "Wealth"],
        };
      }

      // Parse the dob string "10/09/2000" to get the year
      const birthDate = new Date(userData.dob);
      const birthYear = birthDate.getFullYear();
      const currentYear = new Date().getFullYear(); // 2026
      const age = currentYear - birthYear;

      if (age >= 70) {
        return {
          category: "GOLDEN_AGE",
          focusPoints: ["Legacy", "PhysicalHealth", "Serenity", "FamilyMerit"],
          tone: "Reverent & Gentle",
          displayMode: "ACCESSIBILITY_MODE",
        };
      }

      return { category: "GENERAL", focusPoints: ["Career", "Love", "Wealth"] };
    } catch (error) {
      console.error("Error getting user persona:", error);
      return { category: "GENERAL", focusPoints: ["Career", "Love", "Wealth"] };
    }
  };

  const addChat = async (chat: Chat) => {
    try {
      const userId = localStorage.getItem("userId") || chat.userId;
      const token = localStorage.getItem("token");

      if (!userId) {
        toast.error(t("toast.chatContext.loginError"));
        throw new Error("User ID required");
      }

      if (!token) {
        toast.error(t("toast.chatContext.tokenError"));
        throw new Error("Token required");
      }

      console.log("Creating chat with:", {
        userId,
        categoryId: chat.categoryId,
        subCategoryId: chat.subCategoryId,
        userMessage: chat.messages[0]?.content || "",
      });

      const userPersona = getHealjaiPersona();

      // Send first message if exists
      const firstMessage = chat.messages[0]?.content || "";
      console.log("first message ->>", firstMessage);
      const response = await api.post("/backend/chat/create", {
        userId,
        categoryId: chat.categoryId,
        subCategoryId: chat.subCategoryId,
        userMessage: firstMessage,
        userPersona: userPersona,
      });

      console.log("Create chat response:", response.data);

      if (response.data.success) {
        const newChat = convertToChatFormat(response.data.data);
        setChats((prev) => [newChat, ...prev]);
        setCurrentChat(newChat);
        toast.success(
          t("toast.chatContext.chatCreated") || "Chat created successfully",
        );
        return newChat;
      } else {
        throw new Error(response.data.message || "Failed to create chat");
      }
    } catch (error: any) {
      console.error("Failed to create chat:", error);

      // Handle specific error cases
      if (error.response?.status === 401) {
        toast.error(
          t("toast.chatContext.sessionExpired") ||
            "Session expired. Please login again.",
        );
        // Clear localStorage and redirect
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        localStorage.removeItem("userId");
        window.location.href = "/login";
      } else if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error(
          t("toast.chatContext.chatCreationFailed") || "Failed to create chat",
        );
      }

      throw error;
    }
  };

  // FIXED: addMessage function - now properly updates chat with AI response

  const getBirthMemoryPrompt = () => {
    const raw = localStorage.getItem("thai_birth_data");
    if (!raw) return null;

    const data = JSON.parse(raw);

    return `
USER BIRTH DETAILS (ALREADY PROVIDED):
Date of Birth: ${data.dob}
Birth Time: ${data.time || "Unknown"}
Birth Place: ${data.place || "Unknown"}

These details are confirmed.
Do NOT ask for them again.
`.trim();
  };

  const addMessage = async (chatId: string, message: Message) => {
    try {
      console.log("addMessage called with:", { chatId, message });

      const userId = localStorage.getItem("userId");
      const token = localStorage.getItem("token");
      const memoryPrompt = getBirthMemoryPrompt();
      const userPersona = getHealjaiPersona();

      // Prepare request data
      const requestData: any = {
        userMessage: message.content,

        // FIRST take from message (what UI sent)
        categoryId: message.categoryId || currentChat?.categoryId || null,

        subCategoryId:
          message.subCategoryId || currentChat?.subCategoryId || null,

        memory: memoryPrompt || undefined, // only include if exists
        userPersona: userPersona,
      };

      // Only add userId if user is authenticated
      if (userId) {
        requestData.userId = userId;
      }

      // Add chatId for existing chats (not temp ones)
      const isTempChat = chatId.startsWith("t-");
      if (!isTempChat) {
        requestData.chatId = chatId;
      }

      console.log("Sending API request:", requestData);

      const headers: any = {
        "Content-Type": "application/json",
      };

      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      // Make API call
      const response = await api.post("/backend/chat/create", requestData, {
        headers,
      });

      console.log("API Response:", response.data);

      if (response.data.success) {
        const backendChat = response.data.data;

        // Convert backend response to chat format
        const updatedChat = convertToChatFormat(backendChat);

        console.log("Updated chat from API:", updatedChat);

        // IMPORTANT: Update the current chat state
        setCurrentChat(updatedChat);

        // Update chats list - replace the old chat with the new one
        setChats((prev) => {
          const newChats = prev.map((c) =>
            c.id === updatedChat.id ? updatedChat : c,
          );

          // If this is a temp chat that got a real ID from backend, we need to handle it
          if (isTempChat && chatId !== updatedChat.id) {
            // Remove the temp chat and add the real one
            return [updatedChat, ...prev.filter((c) => c.id !== chatId)];
          }

          return newChats;
        });

        console.log("Chat state updated successfully");
        // toast.success("Message sent!");
      } else {
        throw new Error(response.data.message || "Failed to send message");
      }
    } catch (error: any) {
      console.error("Failed to send message:", error);

      const specificErrorMessage =
        error.response?.data?.message ||
        error.message ||
        "An unknown error occurred. Please try again.";

      // Add error message to chat for better UX
      if (currentChat) {
        const errorMessage: Message = {
          id: `error_${Date.now()}`,
          content: specificErrorMessage,
          sender: "ai",
          timestamp: new Date(),
        };

        const chatWithError = {
          ...currentChat,
          messages: [...currentChat.messages, errorMessage],
          updatedAt: new Date(),
        };

        setCurrentChat(chatWithError);
        setChats((prev) =>
          prev.map((c) => (c.id === chatId ? chatWithError : c)),
        );
      }

      // Don't redirect to login for anonymous users
      if (error.response?.status === 401 && userId) {
        // Only redirect if user was supposed to be authenticated
        toast.error(
          t("toast.chatContext.sessionExpired") ||
            "Session expired. Please login again.",
        );
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        localStorage.removeItem("userId");
        window.location.href = "/login";
      } else {
        toast.error(specificErrorMessage);
      }

      throw error;
    }
  };

  const updateMessage = (
    chatId: string,
    messageId: string,
    content: string,
  ) => {
    setCurrentChat((prev) => {
      if (!prev || prev.id !== chatId) return prev;
      return {
        ...prev,
        messages: prev.messages.map((msg) =>
          msg.id === messageId ? { ...msg, content } : msg,
        ),
        updatedAt: new Date(),
      };
    });
    setChats((prev) =>
      prev.map((chat) => {
        if (chat.id === chatId) {
          return {
            ...chat,
            messages: chat.messages.map((msg) =>
              msg.id === messageId ? { ...msg, content } : msg,
            ),
          };
        }
        return chat;
      }),
    );
  };

  const deleteChat = async (chatId: string) => {
    try {
      // Only call API if it's not a temp chat
      if (!chatId.startsWith("t-")) {
        await api.delete(`/backend/chat/${chatId}`);
      }

      setChats((prev) => prev.filter((chat) => chat.id !== chatId));

      if (currentChat?.id === chatId) {
        setCurrentChat(null);
      }

      toast.success(
        t("toast.chatContext.chatDeleted") || "Chat deleted successfully",
      );
      return true;
    } catch (error) {
      console.error("Failed to delete chat:", error);
      toast.error(
        t("toast.chatContext.chatDeleteFailed") || "Failed to delete chat",
      );
      return false;
    }
  };

  const startNewChat = async (
    categoryId?: string,
    subCategoryId?: string,
    initialMessage?: string,
  ): Promise<Chat> => {
    try {
      setIsLoading(true);

      const userId = localStorage.getItem("userId");
      const tempId = "t-" + Date.now().toString();

      const tempChat: Chat = {
        id: tempId,
        title: initialMessage?.substring(0, 30) || "New Chat",
        messages: [],
        categoryId: categoryId || null,
        subCategoryId: subCategoryId || null,
        userId,
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      // If there's an initial message, add it immediately
      if (initialMessage) {
        const userMessage: Message = {
          id: Date.now().toString(),
          content: initialMessage,
          sender: "user",
          timestamp: new Date(),
          categoryId,
          subCategoryId,
        };
        tempChat.messages.push(userMessage);
      }

      setCurrentChat(tempChat);
      setChats((prev) => [tempChat, ...prev]);
      return tempChat;
    } catch (error) {
      console.error("Failed to start new chat:", error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const sendMessage = async (
    content: string,
    chatId?: string,
    categoryId?: string | null,
    subCategoryId?: string | null,
  ) => {
    if (!content.trim() || isLoading) return;

    try {
      setIsTyping(true);

      // 1) Try to find the chat
      let targetChat: Chat | null =
        (chatId ? chats.find((c) => c.id === chatId) : null) || currentChat;

      // ✅ KEY FIX:
      // If chatId exists but chat isn't in state yet (React async update),
      // create a placeholder with SAME chatId (DON'T create a new temp chat)
      if (chatId && !targetChat) {
        const placeholder: Chat = {
          id: chatId,
          title: "New Chat",
          messages: [],
          categoryId: categoryId ?? null,
          subCategoryId: subCategoryId ?? null,
          userId: localStorage.getItem("userId") || undefined,
          createdAt: new Date(),
          updatedAt: new Date(),
        };

        targetChat = placeholder;

        setCurrentChat(placeholder);
        setChats((prev) => {
          const exists = prev.some((c) => c.id === chatId);
          return exists ? prev : [placeholder, ...prev];
        });
      }

      // If still no chat (no chatId + no currentChat), then create one
      if (!targetChat) {
        targetChat = await startNewChat(
          categoryId || undefined,
          subCategoryId || undefined,
        );
      }

      // 2) Add user message optimistically
      const userMessage: Message = {
        id: `user_${Date.now()}`,
        content: content.trim(),
        sender: "user",
        timestamp: new Date(),
        categoryId: categoryId ?? targetChat.categoryId,
        subCategoryId: subCategoryId ?? targetChat.subCategoryId,
      };

      const optimisticChat: Chat = {
        ...targetChat,
        categoryId: categoryId ?? targetChat.categoryId,
        subCategoryId: subCategoryId ?? targetChat.subCategoryId,
        messages: [...(targetChat.messages || []), userMessage],
        updatedAt: new Date(),
      };

      setCurrentChat(optimisticChat);

      setChats((prev) => {
        const exists = prev.some((c) => c.id === optimisticChat.id);
        if (!exists) return [optimisticChat, ...prev];
        return prev.map((c) =>
          c.id === optimisticChat.id ? optimisticChat : c,
        );
      });

      // 3) Backend call (will replace temp chat with real one if needed)
      await addMessage(optimisticChat.id, userMessage);
    } catch (e) {
      console.error("sendMessage error:", e);
    } finally {
      setIsTyping(false);
    }
  };

  const sendMessageStream = async (
    content: string,
    chatId?: string,
    categoryId?: string | null,
    subCategoryId?: string | null,
    spanishTone?: string | null,
    japan3BoxSelf?: object | null,
    japan3BoxPartner?: object | null,
    korea3BoxSelf?: object | null,
    korea3BoxPartner?: object | null,
    gcc3BoxSelf?: object | null,
    gcc3BoxPartner?: object | null,
    indonesia3BoxSelf?: object | null,
    indonesia3BoxPartner?: object | null,
    koreaHybridMode?: string | null,
    japanHybridMode?: string | null,
  ) => {
    if (!content.trim() || isLoading) return null;

    let targetChat: Chat | null =
      (chatId ? chats.find((c) => c.id === chatId) : null) || currentChat;

    try {
      setIsTyping(true);

      if (chatId && !targetChat) {
        const placeholder: Chat = {
          id: chatId,
          title: "New Chat",
          messages: [],
          categoryId: categoryId ?? null,
          subCategoryId: subCategoryId ?? null,
          userId: localStorage.getItem("userId") || undefined,
          createdAt: new Date(),
          updatedAt: new Date(),
        };

        targetChat = placeholder;

        setCurrentChat(placeholder);
        setChats((prev) => {
          const exists = prev.some((c) => c.id === chatId);
          return exists ? prev : [placeholder, ...prev];
        });
      }

      if (!targetChat) {
        targetChat = await startNewChat(
          categoryId || undefined,
          subCategoryId || undefined,
        );
      }

      const userMessage: Message = {
        id: `user_${Date.now()}`,
        content: content.trim(),
        sender: "user",
        timestamp: new Date(),
        categoryId: categoryId ?? targetChat.categoryId,
        subCategoryId: subCategoryId ?? targetChat.subCategoryId,
      };

      let workingChat: Chat = {
        ...targetChat,
        categoryId: categoryId ?? targetChat.categoryId,
        subCategoryId: subCategoryId ?? targetChat.subCategoryId,
        messages: [...(targetChat.messages || []), userMessage],
        updatedAt: new Date(),
      };
      const trimmedTitle = (workingChat.title || "").trim();
      if (!trimmedTitle || trimmedTitle === "New Chat") {
        workingChat = {
          ...workingChat,
          title: userMessage.content.slice(0, 30) || "New Chat",
        };
      }

      setCurrentChat(workingChat);
      setChats((prev) => {
        const exists = prev.some((c) => c.id === workingChat.id);
        if (!exists) return [workingChat, ...prev];
        return prev.map((c) => (c.id === workingChat.id ? workingChat : c));
      });

      const userId = localStorage.getItem("userId");
      const token = localStorage.getItem("token");
      const memoryPrompt = getBirthMemoryPrompt();
      const userPersona = getHealjaiPersona();

      const requestData: any = {
        userMessage: userMessage.content,
        categoryId: userMessage.categoryId || workingChat.categoryId || null,
        subCategoryId:
          userMessage.subCategoryId || workingChat.subCategoryId || null,
        memory: memoryPrompt || undefined,
        userPersona,
      };

      if (spanishTone) {
        requestData.spanishTone = spanishTone;
      }

      if (koreaHybridMode) {
        requestData.koreaHybridMode = koreaHybridMode;
      }

      if (japanHybridMode) {
        requestData.japanHybridMode = japanHybridMode;
      }

      if (japan3BoxSelf) {
        requestData.japan3BoxSelf = japan3BoxSelf;
      }

      if (japan3BoxPartner) {
        requestData.japan3BoxPartner = japan3BoxPartner;
      }

      if (korea3BoxSelf) {
        requestData.korea3BoxSelf = korea3BoxSelf;
      }

      if (korea3BoxPartner) {
        requestData.korea3BoxPartner = korea3BoxPartner;
      }

      if (gcc3BoxSelf) {
        requestData.gcc3BoxSelf = gcc3BoxSelf;
      }

      if (gcc3BoxPartner) {
        requestData.gcc3BoxPartner = gcc3BoxPartner;
      }

      if (indonesia3BoxSelf) {
        requestData.indonesia3BoxSelf = indonesia3BoxSelf;
      }

      if (indonesia3BoxPartner) {
        requestData.indonesia3BoxPartner = indonesia3BoxPartner;
      }

      if (userId) {
        requestData.userId = userId;
      }

      const isTempChat = workingChat.id.startsWith("t-");
      if (!isTempChat) {
        requestData.chatId = workingChat.id;
      }

      const baseUrl = api.defaults.baseURL || "";
      const streamUrl = `${baseUrl}/backend/chat/create?stream=true`;

      const headers: Record<string, string> = {
        "Content-Type": "application/json",
        Accept: "text/event-stream",
      };

      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await fetch(streamUrl, {
        method: "POST",
        headers,
        body: JSON.stringify(requestData),
      });

      if (!response.ok || !response.body) {
        let errMsg = "Stream request failed";
        try {
          const errData = await response.json();
          if (errData?.message) errMsg = errData.message;
        } catch {}
        throw new Error(errMsg);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let aiContent = "";
      const aiMessageId = `ai_stream_${Date.now()}`;
      let hasAddedAi = false;
      let finalChatId: string | null = null;
      let finalSamayPravahGraph: SamayPravahGraph | null = null;
      let finalGccCompatibilityData: any = null;
      let finalJapanCompatibilityData: any = null;
      let finalIndonesiaCompatibilityData: any = null;
      let finalAstriaKoreaV2Data: any = null;
      let finalAstriaJapanHybridData: any = null;
      let finalBhavnaDrishtiData: BhavnaDrishtiData | null = null;
      let finalUpayMargData: UpayMargData | null = null;
      let finalSambandhTaalMelData: SambandhTaalMelData | null = null;
      let finalAstriaIndiaV2Data: AstriaIndiaV2Data | null = null;

      const ensureAiMessage = (initialContent = "") => {
        if (hasAddedAi) return;
        hasAddedAi = true;
        const aiMessage: Message = {
          id: aiMessageId,
          content: initialContent,
          sender: "ai",
          timestamp: new Date(),
          categoryId: workingChat.categoryId,
          subCategoryId: workingChat.subCategoryId,
        };

        workingChat = {
          ...workingChat,
          messages: [...workingChat.messages, aiMessage],
          updatedAt: new Date(),
        };

        setCurrentChat(workingChat);
        setChats((prev) =>
          prev.map((c) => (c.id === workingChat.id ? workingChat : c)),
        );
      };

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";

        for (const part of parts) {
          const lines = part.split("\n");
          const isErrorEvent = lines.some(
            (line) =>
              line.startsWith("event:") &&
              line.replace(/^event:\s*/, "").trim() === "error",
          );

          for (const line of lines) {
            if (!line.startsWith("data:")) continue;
            const dataStr = line.replace(/^data:\s*/, "");
            if (!dataStr) continue;

            let payload: any;
            try {
              payload = JSON.parse(dataStr);
            } catch (err) {
              continue;
            }

            // Backend emits `event: error` + a data frame (e.g. Gemini
            // exhausted its retries) when it can't produce a response.
            // Without this check the data: line still parses as JSON but
            // matches none of the payload shapes below, so it silently
            // fell through to "No response" instead of a visible error.
            if (isErrorEvent) {
              throw new Error(
                payload?.message || "Chat creation failed",
              );
            }

            if (payload?.text) {
              if (!hasAddedAi) {
                ensureAiMessage();
                setIsTyping(false);
              }
              aiContent += payload.text;
              updateMessage(workingChat.id, aiMessageId, aiContent);
            }

            if (payload?.done && payload?.chatId) {
              finalChatId = payload.chatId;
            }
            if (payload?.samayPravahGraph) {
              finalSamayPravahGraph = payload.samayPravahGraph;
            }
            if (payload?.gccCompatibilityData) {
              finalGccCompatibilityData = payload.gccCompatibilityData;
            }
            if (payload?.japanCompatibilityData) {
              finalJapanCompatibilityData = payload.japanCompatibilityData;
            }
            if (payload?.indonesiaCompatibilityData) {
              finalIndonesiaCompatibilityData = payload.indonesiaCompatibilityData;
            }
            if (payload?.astriaKoreaV2Data) {
              finalAstriaKoreaV2Data = payload.astriaKoreaV2Data;
            }
            if (payload?.astriaJapanHybridData) {
              finalAstriaJapanHybridData = payload.astriaJapanHybridData;
            }
            if (payload?.bhavnaDrishtiData) {
              finalBhavnaDrishtiData = payload.bhavnaDrishtiData;
            }
            if (payload?.upayMargData) {
              finalUpayMargData = payload.upayMargData;
            }
            if (payload?.sambandhTaalMelData) {
              finalSambandhTaalMelData = payload.sambandhTaalMelData;
            }
            if (payload?.astriaIndiaV2Data) {
              finalAstriaIndiaV2Data = payload.astriaIndiaV2Data;
            }
          }
        }
      }

      if (!hasAddedAi) {
        ensureAiMessage("No response");
      }

      // Attach samayPravahGraph to the AI message if received in done event
      if (finalSamayPravahGraph) {
        setCurrentChat((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            messages: prev.messages.map((msg) =>
              msg.id === aiMessageId
                ? { ...msg, samayPravahGraph: finalSamayPravahGraph! }
                : msg,
            ),
          };
        });
        setChats((prev) =>
          prev.map((chat) =>
            chat.id !== workingChat.id
              ? chat
              : {
                  ...chat,
                  messages: chat.messages.map((msg) =>
                    msg.id === aiMessageId
                      ? { ...msg, samayPravahGraph: finalSamayPravahGraph! }
                      : msg,
                  ),
                },
          ),
        );
      }

      // Attach astriaKoreaV2Data to the AI message if received in done event
      if (finalAstriaKoreaV2Data) {
        setCurrentChat((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            messages: prev.messages.map((msg) =>
              msg.id === aiMessageId
                ? { ...msg, astriaKoreaV2Data: finalAstriaKoreaV2Data }
                : msg,
            ),
          };
        });
        setChats((prev) =>
          prev.map((chat) =>
            chat.id !== workingChat.id
              ? chat
              : {
                  ...chat,
                  messages: chat.messages.map((msg) =>
                    msg.id === aiMessageId
                      ? { ...msg, astriaKoreaV2Data: finalAstriaKoreaV2Data }
                      : msg,
                  ),
                },
          ),
        );
      }

      // Attach astriaJapanHybridData to the AI message if received in done
      // event — same pattern as astriaKoreaV2Data above.
      if (finalAstriaJapanHybridData) {
        setCurrentChat((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            messages: prev.messages.map((msg) =>
              msg.id === aiMessageId
                ? { ...msg, astriaJapanHybridData: finalAstriaJapanHybridData }
                : msg,
            ),
          };
        });
        setChats((prev) =>
          prev.map((chat) =>
            chat.id !== workingChat.id
              ? chat
              : {
                  ...chat,
                  messages: chat.messages.map((msg) =>
                    msg.id === aiMessageId
                      ? { ...msg, astriaJapanHybridData: finalAstriaJapanHybridData }
                      : msg,
                  ),
                },
          ),
        );
      }

      // Attach bhavnaDrishtiData / upayMargData / sambandhTaalMelData to the
      // AI message if received in the done event — same pattern as
      // samayPravahGraph/astriaKoreaV2Data above. Additive only: existing
      // consumers that ignore these fields are unaffected.
      if (finalBhavnaDrishtiData) {
        setCurrentChat((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            messages: prev.messages.map((msg) =>
              msg.id === aiMessageId
                ? { ...msg, bhavnaDrishtiData: finalBhavnaDrishtiData! }
                : msg,
            ),
          };
        });
        setChats((prev) =>
          prev.map((chat) =>
            chat.id !== workingChat.id
              ? chat
              : {
                  ...chat,
                  messages: chat.messages.map((msg) =>
                    msg.id === aiMessageId
                      ? { ...msg, bhavnaDrishtiData: finalBhavnaDrishtiData! }
                      : msg,
                  ),
                },
          ),
        );
      }

      if (finalUpayMargData) {
        setCurrentChat((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            messages: prev.messages.map((msg) =>
              msg.id === aiMessageId
                ? { ...msg, upayMargData: finalUpayMargData! }
                : msg,
            ),
          };
        });
        setChats((prev) =>
          prev.map((chat) =>
            chat.id !== workingChat.id
              ? chat
              : {
                  ...chat,
                  messages: chat.messages.map((msg) =>
                    msg.id === aiMessageId
                      ? { ...msg, upayMargData: finalUpayMargData! }
                      : msg,
                  ),
                },
          ),
        );
      }

      if (finalSambandhTaalMelData) {
        setCurrentChat((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            messages: prev.messages.map((msg) =>
              msg.id === aiMessageId
                ? { ...msg, sambandhTaalMelData: finalSambandhTaalMelData! }
                : msg,
            ),
          };
        });
        setChats((prev) =>
          prev.map((chat) =>
            chat.id !== workingChat.id
              ? chat
              : {
                  ...chat,
                  messages: chat.messages.map((msg) =>
                    msg.id === aiMessageId
                      ? {
                          ...msg,
                          sambandhTaalMelData: finalSambandhTaalMelData!,
                        }
                      : msg,
                  ),
                },
          ),
        );
      }

      if (finalAstriaIndiaV2Data) {
        setCurrentChat((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            messages: prev.messages.map((msg) =>
              msg.id === aiMessageId
                ? { ...msg, astriaIndiaV2Data: finalAstriaIndiaV2Data! }
                : msg,
            ),
          };
        });
        setChats((prev) =>
          prev.map((chat) =>
            chat.id !== workingChat.id
              ? chat
              : {
                  ...chat,
                  messages: chat.messages.map((msg) =>
                    msg.id === aiMessageId
                      ? { ...msg, astriaIndiaV2Data: finalAstriaIndiaV2Data! }
                      : msg,
                  ),
                },
          ),
        );
      }

      // Attach gccCompatibilityData to the chat
      if (finalGccCompatibilityData) {
        setCurrentChat((prev) => {
          if (!prev) return prev;
          return { ...prev, gccCompatibilityData: finalGccCompatibilityData };
        });
        setChats((prev) =>
          prev.map((chat) =>
            chat.id !== workingChat.id
              ? chat
              : { ...chat, gccCompatibilityData: finalGccCompatibilityData },
          ),
        );
      }

      // Attach japanCompatibilityData to the chat
      if (finalJapanCompatibilityData) {
        setCurrentChat((prev) => {
          if (!prev) return prev;
          return { ...prev, japanCompatibilityData: finalJapanCompatibilityData };
        });
        setChats((prev) =>
          prev.map((chat) =>
            chat.id !== workingChat.id
              ? chat
              : { ...chat, japanCompatibilityData: finalJapanCompatibilityData },
          ),
        );
      }

      // Attach indonesiaCompatibilityData to the chat
      if (finalIndonesiaCompatibilityData) {
        setCurrentChat((prev) => {
          if (!prev) return prev;
          return { ...prev, indonesiaCompatibilityData: finalIndonesiaCompatibilityData };
        });
        setChats((prev) =>
          prev.map((chat) =>
            chat.id !== workingChat.id
              ? chat
              : { ...chat, indonesiaCompatibilityData: finalIndonesiaCompatibilityData },
          ),
        );
      }

      if (finalChatId && finalChatId !== workingChat.id) {
        setCurrentChat((prev) =>
          prev && prev.id === workingChat.id
            ? { ...prev, id: finalChatId }
            : prev,
        );
        setChats((prev) =>
          prev.map((chat) =>
            chat.id === workingChat.id ? { ...chat, id: finalChatId } : chat,
          ),
        );
      }

      // aiContent is accumulated locally as chunks stream in, so it's always
      // current the instant the stream ends — unlike currentChat/chats,
      // which are React state and may not have re-rendered into callers'
      // refs yet by the time this await resolves. Callers that need the
      // final text right after sendMessageStream returns (e.g. GCC v2 tab
      // results) should use this return value instead of reading state.
      return aiContent;
    } catch (e: any) {
      console.error("sendMessageStream error:", e);

      const specificErrorMessage =
        e.message || "An unknown stream error occurred. Please try again.";

      if (currentChat) {
        const errorMessage: Message = {
          id: `error_${Date.now()}`,
          content: specificErrorMessage,
          sender: "ai",
          timestamp: new Date(),
        };

        const chatWithError = {
          ...currentChat,
          messages: [...currentChat.messages, errorMessage],
          updatedAt: new Date(),
        };

        setCurrentChat(chatWithError);
        setChats((prev) =>
          prev.map((c) => (c.id === chatId ? chatWithError : c)),
        );
      }

      toast.error(specificErrorMessage);
      return null;
    } finally {
      setIsTyping(false);
    }
  };

  React.useEffect(() => {
    const userId = localStorage.getItem("userId");
    if (userId) {
      fetchChats();
    }
  }, [fetchChats]);

  return (
    <ChatContext.Provider
      value={{
        chats,
        currentChat,
        isLoading,
        isTyping,
        addChat,
        setCurrentChat,
        addMessage,
        updateMessage,
        deleteChat,
        startNewChat,
        sendMessage,
        sendMessageStream,
        fetchChats,
        fetchSingleChat,
        openChat,
        resetChatState,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
};
